import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Button, Form, Input, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EditOutlined } from "@ant-design/icons";
import * as Yup from "yup";
import TextArea from "antd/lib/input/TextArea";
import { DefaultOptionType } from "antd/lib/select";
import moment from "moment";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import getValidationErrors from "../../../utils/getValidationErrors";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";
import { IUser } from "../../../pages/User";
import { removeNullKeys } from "../../../utils/formatters";
import InputCurrentBRL from "../../InputMask/InputCurrentBRL";

interface IEditFieldModalProps {
  userUpdate: IUser;
  fieldName: string;
  handleUpdateProject: (project: IUser) => void;
  validationRule?: Yup.StringSchema<string>;
  inputType?: "INPUT" | "TEXTAREA" | "SELECT" | "DATE" | "MASKBRL";
  selectOptions?: DefaultOptionType[];
  title: string;
  exist_thirteenth_salary?: boolean;
}

interface IEditFieldModalFormData {
  [key: string]: string;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const EditFieldModal: React.FC<IEditFieldModalProps> = ({
  userUpdate,
  fieldName,
  handleUpdateProject,
  validationRule,
  inputType = "INPUT",
  selectOptions = [],
  title,
}) => {
  const { t } = useTranslation(["consultants"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [valueInput, setValueInput] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();
  const { user } = useAuth();

  const { addToast } = useToast();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role]
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Um erro occoreu ao alterar o campo, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao cancelar",
        description,
      });
    },
    [addToast]
  );

  const onInputChange = (value: string) => {
    setValueInput(value);
  };

  const handleSendRequest = async (FormData: IEditFieldModalFormData) => {
    let data = FormData;
    if (inputType === "MASKBRL") {
      data = {
        ...FormData,
        [fieldName]: valueInput,
      };
      /* const clearKey = ClearMask(key); */
    }
    const paid_clt =
      (userUpdate.hiring_model === "CLT_TEMP" ||
        userUpdate.hiring_model === "CLT") &&
      data?.base_salary
        ? (Number(data.base_salary?.replace(",", ".")) * 0.8).toFixed(2)
        : null;
    try {
      setLoading(true);
      const payload = {
        ...data,
        base_salary: data.base_salary?.replace(",", ".") || null,
        thirteenth_salary: data?.base_salary?.replace(",", ".") || null,
        paid_time_off: data?.base_salary
          ? (Number(data.base_salary?.replace(",", ".")) / 2).toFixed(2)
          : null,
        paid_clt,
        modificatoryId: user.id,
      };
      removeNullKeys(payload);

      const response = await api.patch(`/users/${userUpdate.id}/consultant`, {
        ...payload,
      });

      handleUpdateProject(response.data);

      addToast({
        type: "success",
        title: "Campo alterado",
        description: "O projeto foi atualizado com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleSubmit = useCallback(
    async (data: IEditFieldModalFormData) => {
      try {
        if (validationRule) {
          const shape = {} as any;
          shape[fieldName] = validationRule;
          const schema = Yup.object().shape(shape);

          await schema.validate(data, {
            abortEarly: false,
          });
        }
        handleSendRequest(data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [fieldName, handleSendRequest, validationRule]
  );

  return isLoggedUserManager ? (
    <>
      <Modal
        // title={t("cancel")}
        title={`Editar ${title}`}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        forceRender
      >
        <Form
          {...layout}
          form={form}
          name="edit-field-form"
          onFinish={handleSubmit}
        >
          {inputType === "INPUT" && (
            <FormItem
              name={fieldName}
              label={t("label")}
              errors={validationErrors}
            >
              <Input
                defaultValue={`${userUpdate[fieldName as keyof IUser] || ""}`}
              />
            </FormItem>
          )}
          {inputType === "MASKBRL" && (
            <FormItem
              name={fieldName}
              label={t("label")}
              errors={validationErrors}
            >
              <InputCurrentBRL
                placeholder="R$ 0,00"
                name={fieldName}
                onInputChange={onInputChange}
                type="text"
                defaultValue={`${userUpdate[fieldName as keyof IUser] || ""}`}
                err={((validationErrors as unknown) as any)?.[fieldName]}
              />
            </FormItem>
          )}

          {inputType === "TEXTAREA" && (
            <FormItem
              name={fieldName}
              label={t("value")}
              errors={validationErrors}
            >
              <TextArea
                rows={10}
                defaultValue={`${userUpdate[fieldName as keyof IUser] || ""}`}
              />
            </FormItem>
          )}

          {inputType === "SELECT" && (
            <FormItem
              name={fieldName}
              label={t("label")}
              errors={validationErrors}
            >
              <Select
                allowClear
                defaultValue={`${userUpdate[fieldName as keyof IUser] || ""}`}
                style={{ width: "100%" }}
                options={selectOptions}
              />
            </FormItem>
          )}

          {inputType === "DATE" && (
            <FormItem
              name={fieldName}
              label={t("label")}
              errors={validationErrors}
            >
              <DatePicker
                style={{ width: "100%" }}
                defaultValue={
                  moment(`${userUpdate[fieldName as keyof IUser]}`).isValid()
                    ? moment(`${userUpdate[fieldName as keyof IUser]}`)
                    : undefined
                }
                format="DD/MM/YYYY"
              />
            </FormItem>
          )}
        </Form>
      </Modal>
      <Button
        type="link"
        shape="circle"
        icon={<EditOutlined />}
        onClick={() => setVisible((oldVisible) => !oldVisible)}
      />
    </>
  ) : (
    <></>
  );
};

export default EditFieldModal;
